<template>
  <AppDialog
    :is-open="isOpen"
    :ok-text="$t('common.DocumentsPicker.addDocuments')"
    :ok-loading="loading"
    :ok-disabled="selectedDocuments.length === 0 || loading"
    :cancel-disabled="loading"
    size="xxl"
    header-divider
    data-locator="documents-picker-dialog"
    @ok="confirm"
    @cancel="close"
  >
    <template #title>
      {{ $t('common.DocumentsPicker.addDocumentsTitle') }}
    </template>

    <template #body>
      <div
        class="d-flex flex-column ga-6 shared-documents-picker-dialog__body"
        data-locator="documents-picker-body"
      >
        <p
          v-if="maxSelection > 0"
          class="documents-picker__max-selection-hint text--small text--regular mb-0"
          data-locator="documents-picker-max-selection-hint"
        >
          {{ $t('common.DocumentsPicker.maxSelectionHint', maxSelection, { count: maxSelection }) }}
        </p>

        <app-bulk-list
          v-if="selectedDocuments.length > 0"
          :items="selectedDocuments"
          :items-name="$t('common.DocumentsPicker.selectedFiles')"
          data-locator="documents-picker-selected-bulk-list"
        >
          <template #default="{ item }">
            <DocumentsPickerSelectedItem
              :file="item"
              locator-prefix="picker-selected"
              full-width
              embedded
              @remove="removeSelected"
            />
          </template>
        </app-bulk-list>

        <v-row
          class="documents-picker__toolbar"
          align="center"
          no-gutters
        >
          <v-col cols="12" md="auto">
            <DocumentsPickerSegmentedControl
              v-model="activeTab"
              @change="onTabChanged"
            />
          </v-col>

          <v-col
            v-if="enableSearch"
            cols="12"
            md
            class="documents-picker__toolbar-right"
          >
            <div data-locator="documents-picker-search">
              <SearchTextField
                v-model="search"
                :disabled="loading"
                :tooltipText="$t('common.DocumentsPicker.searchPlaceholder')"
              />
            </div>
          </v-col>
        </v-row>

        <div class="overflow-auto" data-locator="documents-picker-content">
          <v-skeleton-loader
            v-if="loading && items.length === 0 && activeTab === TABS.DOCUMENTS"
            type="table"
          />

          <!-- DOCUMENTS TAB -->
          <template v-else-if="activeTab === TABS.DOCUMENTS">
            <DocumentsPickerBreadcrumb
              v-if="showBreadcrumb && breadcrumb.length"
              class="mb-4"
              :breadcrumb="breadcrumb"
              :current-folder-id="currentFolderId"
              @go-to="goToBreadcrumb"
            />

            <AppCardEmptyState
              v-if="!loading && items.length === 0 && isSearchMode"
              :title="$t('common.DocumentsPicker.noSearchResults')"
              :text="$t('common.DocumentsPicker.noSearchResultsDescription')"
              data-locator="picker-documents-search-empty-state"
            />

            <AppCardEmptyState
              v-else-if="!loading && items.length === 0"
              :title="$t('common.DocumentsPicker.noFiles')"
              :text="$t('common.DocumentsPicker.noFilesDescription')"
              data-locator="picker-documents-empty-state"
            />

            <DocumentsPickerTable
              v-else
              class="px-2"
              :items="items"
              :loading="loading"
              :total="total"
              :page="page"
              :page-size="pageSize"
              :max-selection="maxSelection"
              v-model="selectedDocuments"
              @update:page="onPageChange"
              @update:pageSize="onPageSizeChange"
              @open-folder="openFolder"
            />
          </template>

          <!-- QA ATTACHMENTS TAB -->
          <template v-else>
            <AppCardEmptyState
              data-locator="picker-qa-empty-state"
              :title="$t('common.DocumentsPicker.noQaAttachments')"
              :text="$t('common.DocumentsPicker.noQaAttachmentsDescription')"
            />
          </template>
        </div>
      </div>
    </template>
  </AppDialog>
</template>

<script>
import { debounce } from 'lodash-es'
import { mapGetters } from 'vuex'

import AppCardEmptyState from '@/common/AppCardEmptyState.vue'
import AppDialog from '@/common/dialogs/AppDialog.vue'
import SearchTextField from '@/common/SearchTextField.vue'
import { toast } from '@/common/utils/toast'

import { listFolderPaged } from './documentsPicker.service'
import DocumentsPickerBreadcrumb from './DocumentsPickerBreadcrumb.vue'
import DocumentsPickerSegmentedControl from './DocumentsPickerSegmentedControl.vue'
import DocumentsPickerSelectedItem from './DocumentsPickerSelectedItem.vue'
import DocumentsPickerTable from './DocumentsPickerTable.vue'

const TABS = {
  DOCUMENTS: 'documents',
  QA_ATTACHMENTS: 'qa_attachments',
}

export default {
  name: 'SharedDocumentsPickerDialog',
  components: {
    AppDialog,
    SearchTextField,
    DocumentsPickerBreadcrumb,
    DocumentsPickerSelectedItem,
    DocumentsPickerTable,
    DocumentsPickerSegmentedControl,
    AppCardEmptyState,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    modelValue: {
      type: Array,
      default: () => ([]),
    },
    rootFolderId: {
      type: Number,
      default: 0,
    },
    initialFolderId: {
      type: Number,
      default: 0,
    },
    ignoreTodoFolder: {
      type: Boolean,
      default: true,
    },
    defaultTab: {
      type: String,
      default: TABS.DOCUMENTS,
    },
    allowedTypes: {
      type: Array,
      default: () => ['file'],
      validator: (val) => Array.isArray(val) && val.every(t => ['file', 'folder'].includes(t)),
    },
    enableSearch: {
      type: Boolean,
      default: true,
    },
    maxSelection: {
      type: Number,
      default: 0,
      validator: (val) => Number.isInteger(val) && val >= 0,
    },
  },
  emits: ['update:isOpen', 'update:modelValue', 'confirm'],
  data () {
    return {
      TABS,
      activeTab: this.defaultTab,
      loading: false,
      currentFolderId: this.initialFolderId || this.rootFolderId,
      breadcrumb: [],
      items: [],
      total: 0,
      page: 1,
      pageSize: 25,
      searchQuery: '',
    }
  },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
    showBreadcrumb () {
      return this.activeTab === TABS.DOCUMENTS && !this.isSearchMode
    },
    isSearchMode () {
      if (!this.enableSearch) return false
      return Boolean(this.searchQuery.trim())
    },
    search: {
      get () {
        return this.searchQuery
      },
      set: debounce(function (val) {
        this.searchQuery = val
        this.page = 1
        this.getDocuments()
      }, 500),
    },
    selectedDocuments: {
      get () {
        return this.modelValue || []
      },
      set (val) {
        const allowed = new Set(this.allowedTypes)
        let sanitized = (val || []).filter(i => i && allowed.has(i.type))
        if (this.maxSelection > 0) {
          sanitized = sanitized.slice(0, this.maxSelection)
        }
        this.$emit('update:modelValue', sanitized)
      },
    },
  },
  watch: {
    isOpen (v) {
      if (v) {
        this.resetStateOnOpen()

        if (this.activeTab === TABS.DOCUMENTS) {
          this.getDocuments()
        }
      }
    },
    page () {
      if (this.activeTab === TABS.DOCUMENTS) {
        this.getDocuments()
      }
    },
    pageSize () {
      if (this.activeTab === TABS.DOCUMENTS) {
        this.page = 1
        this.getDocuments()
      }
    },
    allowedTypes: {
      handler () {
        const allowed = new Set(this.allowedTypes)
        const sanitized = (this.modelValue || []).filter(d => d && allowed.has(d.type))

        const key = (arr) => (arr || []).map(d => `${d.type}-${d.id}`).sort().join('|')
        if (key(sanitized) === key(this.modelValue || [])) return

        this.$emit('update:modelValue', sanitized)
      },
      deep: true,
    },
  },
  methods: {
    resetStateOnOpen () {
      this.activeTab = this.defaultTab
      this.currentFolderId = this.initialFolderId || this.rootFolderId
      this.page = 1
      this.items = []
      this.total = 0
      this.breadcrumb = []
      if (this.enableSearch) {
        this.searchQuery = ''
      }
    },
    onTabChanged (tab) {
      if (tab === TABS.DOCUMENTS) {
        this.page = 1
        this.searchQuery = ''
        this.getDocuments()
      }
    },
    close () {
      this.$emit('update:isOpen', false)
    },
    confirm () {
      this.$emit('confirm', this.selectedDocuments)
      this.close()
    },
    async getDocuments () {
      if (this.activeTab !== TABS.DOCUMENTS) return

      this.loading = true
      try {
        const res = await listFolderPaged({
          mnemo: this.roomMnemo,
          folderId: this.currentFolderId,
          page: this.page,
          pageSize: this.pageSize,
          search: this.enableSearch ? this.searchQuery.trim() || undefined : undefined,
          rootFolderId: this.rootFolderId,
          ignoreTodoFolder: this.ignoreTodoFolder,
        })

        this.breadcrumb = res.breadcrumb?.length
          ? res.breadcrumb
          : [{ id: this.rootFolderId, name: this.$t('common.documents.rootFolder') }]

        this.items = res.page.items
        this.total = res.page.total
      } catch (error) {
        console.error(error)
        toast.error(this.$t('common.DocumentsPicker.getDocumentsError'))
      } finally {
        this.loading = false
      }
    },
    onPageChange (nextPage) {
      this.page = nextPage
    },
    onPageSizeChange (nextSize) {
      this.pageSize = nextSize
      this.page = 1
    },
    openFolder (folder) {
      this.currentFolderId = folder.id
      this.page = 1

      if (this.isSearchMode) {
        this.searchQuery = ''
      }

      this.getDocuments()
    },
    goToBreadcrumb (b) {
      if (!b) return
      if (String(b.id) === String(this.currentFolderId)) return

      this.currentFolderId = b.id
      this.page = 1
      this.getDocuments()
    },
    removeSelected (doc) {
      this.selectedDocuments = this.selectedDocuments.filter(
        d => d.id !== doc.id || d.type !== doc.type,
      )
    },
  },
}
</script>

<style scoped lang="scss">
.shared-documents-picker-dialog__body {
  padding-top: 14px;
  padding-bottom: 4px;
  height: min(720px, 70vh);
}

.documents-picker__toolbar {
  flex: 0 0 auto !important;
  margin: 0 !important;
}

.documents-picker__max-selection-hint {
  flex: 0 0 auto;
  color: rgb(var(--v-theme-text-tertiary));
}

.documents-picker__toolbar-right {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 959px) {
  .documents-picker__toolbar-right {
    justify-content: stretch;
    margin-top: 12px;
  }
}
</style>
